/**
 * Fast data path for the SFUT Legacy featured strip.
 *
 * Why Legacy was slower than Olympic Series (measured from WSL, 2026-10-24):
 *  - same explorer API (~0.35-0.7 s), but 10 of 32 Legacy tokens (22-31) have no indexed
 *    metadata, so each needed tokenURI eth_call -> metadata JSON -> image, one after another;
 *  - Legacy images are full-size uploads (up to 2.7 MB) while Olympic's are 60-115 KB;
 *  - the strip only started fetching after the tab was clicked, then waited for idle.
 *
 * Now: a generated manifest (src/data/sfutLegacyManifest.ts) carries metadata and 480px WebP
 * thumbnails served from our own CDN (public/sfut-legacy/), owners and new mints still come
 * live from the explorer, unknown tokens are hydrated with one batched RPC call and raced
 * gateways, results are cached in localStorage, and the gallery prefetches while idle.
 */
import { SFUT_LEGACY_COLLECTION, SFUT_LEGACY_EXPLORER_INSTANCES_URL } from '../data/sfutLegacy';
import { SFUT_LEGACY_MANIFEST, type SfutLegacyManifestEntry } from '../data/sfutLegacyManifest';
import { toPinataMediaUrl } from './ipfs';
import {
  hydrateMissingMetadata,
  needsMetadataFallback,
  type ExplorerInstance,
} from './sfutLegacyMetadata';

export const SFUT_LEGACY_CACHE_KEY = 'sf.sfutLegacy.feed.v1';
export const SFUT_LEGACY_CACHE_TTL_MS = 10 * 60 * 1000;
export const SFUT_LEGACY_EXPLORER_TIMEOUT_MS = 6000;

export type LegacyItem = ExplorerInstance & { thumb?: string };

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
type Deps = {
  fetchImpl?: typeof fetch;
  storage?: StorageLike | null;
  now?: () => number;
  manifest?: Record<string, SfutLegacyManifestEntry>;
  hydrate?: (items: ExplorerInstance[]) => Promise<ExplorerInstance[]>;
};

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Fill metadata/thumbnail from the manifest; keep live fields (owner) from the explorer. */
export function mergeWithManifest(
  items: ExplorerInstance[],
  manifest: Record<string, SfutLegacyManifestEntry> = SFUT_LEGACY_MANIFEST
): LegacyItem[] {
  return items.map((item) => {
    const entry = manifest[String(item.id)];
    if (!entry) return item;
    const metadata =
      needsMetadataFallback(item) && (entry.name || entry.image)
        ? { name: entry.name, description: entry.description, image: entry.image }
        : item.metadata;
    return {
      ...item,
      metadata,
      image_url: item.image_url || entry.image || null,
      ...(entry.thumb ? { thumb: entry.thumb } : {}),
    };
  });
}

/** Manifest-only items (newest first) for when the explorer is down or slow. */
export function manifestItems(
  manifest: Record<string, SfutLegacyManifestEntry> = SFUT_LEGACY_MANIFEST
): LegacyItem[] {
  return mergeWithManifest(
    Object.keys(manifest)
      .sort((a, b) => Number(b) - Number(a))
      .map((id) => ({ id, metadata: null, image_url: null }))
  , manifest);
}

/** Just what the cards render; keeps the cache small. */
function slim(item: LegacyItem): LegacyItem {
  const meta = (item.metadata ?? {}) as Record<string, unknown>;
  const owner = (item.owner as { hash?: string } | undefined)?.hash;
  return {
    id: String(item.id),
    image_url: item.image_url ?? null,
    metadata: {
      ...(typeof meta.name === 'string' ? { name: meta.name } : {}),
      ...(typeof meta.description === 'string' ? { description: meta.description.slice(0, 200) } : {}),
      ...(typeof meta.image === 'string' ? { image: meta.image } : {}),
    },
    ...(owner ? { owner: { hash: owner } } : {}),
    ...(item.thumb ? { thumb: item.thumb } : {}),
  };
}

export function readCachedSfutLegacy(
  storage: StorageLike | null = defaultStorage(),
  now = Date.now()
): LegacyItem[] | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SFUT_LEGACY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: number; items?: LegacyItem[] };
    if (!parsed.at || now - parsed.at > SFUT_LEGACY_CACHE_TTL_MS || !Array.isArray(parsed.items)) return null;
    return parsed.items.length ? parsed.items : null;
  } catch {
    return null;
  }
}

function writeCache(storage: StorageLike | null, items: LegacyItem[], now: number) {
  if (!storage) return;
  try {
    storage.setItem(SFUT_LEGACY_CACHE_KEY, JSON.stringify({ at: now, items: items.map(slim) }));
  } catch {
    /* quota / private mode */
  }
}

async function fetchExplorer(fetchImpl: typeof fetch): Promise<ExplorerInstance[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SFUT_LEGACY_EXPLORER_TIMEOUT_MS);
  try {
    const res = await fetchImpl(SFUT_LEGACY_EXPLORER_INSTANCES_URL, {
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { items?: ExplorerInstance[] };
    return Array.isArray(data?.items) ? data.items : [];
  } finally {
    clearTimeout(timer);
  }
}

/** All Legacy items: cache -> explorer + manifest (+ batched hydrate for unknown tokens). */
export async function loadSfutLegacyItems(deps: Deps = {}): Promise<LegacyItem[]> {
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  const now = deps.now ?? Date.now;
  const cached = readCachedSfutLegacy(storage, now());
  if (cached) return cached;

  const manifest = deps.manifest ?? SFUT_LEGACY_MANIFEST;
  let live: ExplorerInstance[];
  try {
    live = await fetchExplorer(deps.fetchImpl ?? fetch);
  } catch {
    return manifestItems(manifest);
  }
  if (!live.length) return manifestItems(manifest);
  let items: LegacyItem[] = mergeWithManifest(live, manifest);
  if (items.some(needsMetadataFallback)) {
    const hydrate =
      deps.hydrate ?? ((list: ExplorerInstance[]) => hydrateMissingMetadata(SFUT_LEGACY_COLLECTION, list));
    items = await hydrate(items);
  }
  writeCache(storage, items, now());
  return items;
}

export function pickRandom<T>(items: T[], n: number, rand: () => number = Math.random): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

// One load and one random pick per page view, shared by the idle prefetch and the strip,
// so the images the prefetch warmed are exactly the ones the strip shows.
let inflight: Promise<LegacyItem[]> | null = null;
const picks = new Map<number, LegacyItem[]>();

function sharedLoad(deps?: Deps): Promise<LegacyItem[]> {
  if (!inflight) {
    inflight = loadSfutLegacyItems(deps).catch((err) => {
      inflight = null;
      throw err;
    });
  }
  return inflight;
}

/** Picks available right now (memory or fresh localStorage cache), else null. */
export function getSfutLegacyPicksSync(maxItems: number): LegacyItem[] | null {
  const hit = picks.get(maxItems);
  if (hit) return hit;
  const cached = readCachedSfutLegacy();
  if (!cached) return null;
  const picked = pickRandom(cached, maxItems);
  picks.set(maxItems, picked);
  return picked;
}

export async function loadSfutLegacyPicks(maxItems: number, deps?: Deps): Promise<LegacyItem[]> {
  const ready = picks.get(maxItems);
  if (ready) return ready;
  const items = await sharedLoad(deps);
  const picked = picks.get(maxItems) ?? pickRandom(items, maxItems);
  picks.set(maxItems, picked);
  return picked;
}

/** Card image: our thumbnail first, the Filebase-first gateway image as fallback. */
export function legacyImage(item: LegacyItem): { src: string | null; fallback: string | null } {
  const meta = item.metadata as { image?: unknown } | null | undefined;
  const gateway =
    toPinataMediaUrl(item.image_url ?? null) ||
    toPinataMediaUrl(typeof meta?.image === 'string' ? meta.image : null);
  return item.thumb ? { src: item.thumb, fallback: gateway } : { src: gateway, fallback: null };
}

/** Warm data + images before the SFUT Legacy tab is opened. Safe to call repeatedly. */
export async function prefetchSfutLegacy(maxItems: number): Promise<void> {
  const picked = await loadSfutLegacyPicks(maxItems);
  if (typeof Image === 'undefined') return;
  for (const item of picked) {
    const { src } = legacyImage(item);
    if (!src) continue;
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  }
}

/** Tests only. */
export function resetSfutLegacyFeedForTests(): void {
  inflight = null;
  picks.clear();
}
